import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeSelectFeatureFlags } from 'reducers/featureFlags/selectors';
import { createContext, useContext, useEffect } from 'react';
import {
  makeSelectUser,
  makeSelectMarketplaceApiKey,
  makeSelectLoading,
} from 'reducers/user/selectors';
import * as authActions from 'reducers/auth/reducer';
import * as userActions from 'reducers/user/actions';
import {
  makeSelectMasquerade,
  makeSelectFeatures,
  makeSelectMasqueradeScope,
} from 'reducers/auth/selectors';
import { isImmutable } from 'immutable';
import Session from 'utils/Session';
import { Layout } from '@capitalise/ui-design-system';
import { useAuthContextAlpha } from 'RemoteAuth/Auth';
import { meRequest } from 'reducers/user/reducer';
import { FeatureFlagsContext } from 'hooks/useFeatureFlags';

const BUSINESS_PRO = 'Business-Pro';
const BUSINESS_FREE = 'Business-Free';
const BUSINESS_FREE_ZA = 'Business-Free-ZA';

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({
  userImmutable,
  isMasquerade,
  loading,
  featureFlagsState,
  flagsState,
  logout,
  apiKey,
  requestApiKey,
  children,
  masqueradeScope,
  refreshMeData,
}) => {
  const { setUser } = useAuthContextAlpha();
  useEffect(() => {
    requestApiKey();
  }, []);

  useEffect(() => {
    const user = isImmutable(userImmutable) ? userImmutable?.toJS() : null;
    if (user) {
      const isPro =
        user?.membership?.level &&
        user?.membership?.level?.name !== BUSINESS_FREE &&
        user?.membership?.level?.name !== BUSINESS_FREE_ZA;
      setUser({ ...user, isPro });
    }
  }, [userImmutable]);

  const logoutMiddleware = () => {
    localStorage.clear();
    logout();
  };

  const user = isImmutable(userImmutable) ? userImmutable?.toJS() : {};
  user.masquerade = isMasquerade;
  user.masqueradeScope = masqueradeScope;
  const flags = isImmutable(flagsState) ? flagsState?.toJS() : {};
  const featureFlags = {
    ...featureFlagsState,
    ...flags,
    hasDirector: localStorage.getItem('directors-progress'),
  };
  const isPro =
    user.membership?.level &&
    user.membership?.level?.name !== BUSINESS_FREE &&
    user.membership?.level?.name !== BUSINESS_FREE_ZA;

  const membershipLevel = user.membership?.level?.name;

  const demasquerade = () => {
    Session.demasqueradeFromStorage();
    window.location.href = `${window._env_.API_DOMAIN}/demasquerade`;
  };

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      <AuthContext.Provider
        value={{
          logout: logoutMiddleware,
          user,
          apiKey,
          demasquerade,
          loading,
          isPro,
          membershipLevel,
          refreshMeData,
        }}
      >
        {children}
      </AuthContext.Provider>
    </FeatureFlagsContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  userImmutable: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  featureFlagsState: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  flagsState: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  isMasquerade: PropTypes.bool,
  masqueradeScope: PropTypes.string,
  loading: PropTypes.bool,
  logout: PropTypes.func,
  apiKey: PropTypes.string,
  requestApiKey: PropTypes.func,
  refreshMeData: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  userImmutable: makeSelectUser(),
  featureFlagsState: makeSelectFeatureFlags(),
  flagsState: makeSelectFeatures(),
  apiKey: makeSelectMarketplaceApiKey(),
  isMasquerade: makeSelectMasquerade(),
  masqueradeScope: makeSelectMasqueradeScope(),
  loading: makeSelectLoading(),
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(authActions.logout()),
  requestApiKey: () => dispatch(userActions.requestApiKey()),
  refreshMeData: () => dispatch(meRequest()),
});

export const ReduxEnhancedAuthProvider = connect(mapStateToProps, mapDispatchToProps)(AuthProvider);

const withAuth = (Component) => (props) =>
  (
    <Layout>
      <ReduxEnhancedAuthProvider>
        <Component {...props} />
      </ReduxEnhancedAuthProvider>
    </Layout>
  );

export default withAuth;
